import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Gauge,
  Smartphone,
  Monitor,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Zap,
  Image,
  Code,
  Clock,
  FileText,
  ArrowRight,
  History
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score color helper
const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  return 'text-red-500';
};

const getScoreBg = (score) => {
  if (score >= 90) return 'bg-green-500/10 border-green-500/30';
  if (score >= 50) return 'bg-yellow-500/10 border-yellow-500/30';
  return 'bg-red-500/10 border-red-500/30';
};

const getScoreLabel = (score) => {
  if (score >= 90) return 'Bun';
  if (score >= 50) return 'Mediu';
  return 'Slab';
};

// Score gauge component
const ScoreGauge = ({ score, label, size = 'normal' }) => {
  const isLarge = size === 'large';
  const circumference = isLarge ? 251.2 : 175.84; // 2 * PI * r
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className={`relative ${isLarge ? 'w-32 h-32' : 'w-20 h-20'}`}>
      <svg className="transform -rotate-90 w-full h-full">
        <circle
          cx="50%"
          cy="50%"
          r={isLarge ? 40 : 28}
          stroke="currentColor"
          strokeWidth={isLarge ? 8 : 6}
          fill="transparent"
          className="text-muted/20"
        />
        <circle
          cx="50%"
          cy="50%"
          r={isLarge ? 40 : 28}
          stroke="currentColor"
          strokeWidth={isLarge ? 8 : 6}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={getScoreColor(score)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-bold ${isLarge ? 'text-2xl' : 'text-lg'} ${getScoreColor(score)}`}>
          {score}
        </span>
        {label && <span className="text-xs text-muted-foreground">{label}</span>}
      </div>
    </div>
  );
};

// Recommendation item component
const RecommendationItem = ({ rec, onOptimize, optimizing }) => {
  const getIcon = (id) => {
    if (id.includes('image') || id.includes('webp') || id.includes('offscreen')) return Image;
    if (id.includes('render') || id.includes('css') || id.includes('js') || id.includes('minif')) return Code;
    if (id.includes('time') || id.includes('speed') || id.includes('cache')) return Clock;
    if (id.includes('lazy') || id.includes('load')) return Zap;
    return FileText;
  };
  
  const getScoreBadge = (score) => {
    if (score === null || score === undefined) return null;
    if (score >= 0.9) return { class: 'bg-green-500/10 text-green-500', text: 'Bun' };
    if (score >= 0.5) return { class: 'bg-yellow-500/10 text-yellow-500', text: 'Mediu' };
    return { class: 'bg-red-500/10 text-red-500', text: 'Slab' };
  };
  
  const Icon = getIcon(rec.id);
  const scoreBadge = getScoreBadge(rec.score);
  
  const getFixTypeLabel = (type) => {
    const labels = {
      'cache': 'Cache & Viteză',
      'images': 'Optimizare Imagini',
      'minify': 'Minificare CSS/JS',
      'lazyload': 'Lazy Loading',
      'optimization': 'Optimizare Generală'
    };
    return labels[type] || type;
  };
  
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors border border-transparent hover:border-primary/20">
      <div className={`p-2 rounded-lg ${rec.auto_fixable ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
        <Icon className={`w-4 h-4 ${rec.auto_fixable ? 'text-green-500' : 'text-yellow-500'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm">{rec.title}</p>
          {scoreBadge && (
            <Badge className={`text-xs ${scoreBadge.class}`}>{scoreBadge.text}</Badge>
          )}
          {rec.auto_fixable && (
            <Badge className="text-xs bg-green-500/10 text-green-500 border-green-500/20">
              <Zap className="w-3 h-3 mr-1" />
              Auto-fix
            </Badge>
          )}
        </div>
        {rec.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{rec.description}</p>
        )}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {rec.display_value && (
            <span className="text-xs text-muted-foreground">
              Potențial: <span className="text-primary">{rec.display_value}</span>
            </span>
          )}
          {rec.savings_ms > 0 && (
            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-500 border-green-500/20">
              -{Math.round(rec.savings_ms)}ms
            </Badge>
          )}
          {rec.fix_type && (
            <span className="text-xs text-muted-foreground">
              Fix: {getFixTypeLabel(rec.fix_type)}
            </span>
          )}
        </div>
      </div>
      {rec.auto_fixable && onOptimize && (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-green-500/50 text-green-500 hover:bg-green-500/10"
          onClick={() => onOptimize(rec.fix_type)}
          disabled={optimizing}
        >
          {optimizing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>
              <Zap className="w-3 h-3 mr-1" />
              Fix
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default function PageSpeedPage() {
  const [analyzing, setAnalyzing] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [allSitesData, setAllSitesData] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('mobile');
  const [historyPeriod, setHistoryPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  
  const { getAuthHeaders } = useAuth();
  const { currentSite, sites } = useSite();

  useEffect(() => {
    if (currentSite?.id) {
      fetchLatestResult();
      fetchHistory();
    }
    fetchAllSitesData();
  }, [currentSite?.id]);

  // Separate effect for history period changes
  useEffect(() => {
    if (currentSite?.id) {
      fetchHistory();
    }
  }, [currentSite?.id, historyPeriod]);

  const fetchLatestResult = async () => {
    if (!currentSite?.id) return;
    
    try {
      const response = await axios.get(
        `${API}/pagespeed/latest/${currentSite.id}`,
        { headers: getAuthHeaders() }
      );
      if (response.data) {
        setCurrentResult(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch latest PageSpeed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!currentSite?.id) return;
    
    try {
      const response = await axios.get(
        `${API}/pagespeed/history/${currentSite.id}?days=${historyPeriod}`,
        { headers: getAuthHeaders() }
      );
      setHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch PageSpeed history:', error);
      setHistory([]);
    }
  };

  const fetchAllSitesData = async () => {
    try {
      const response = await axios.get(
        `${API}/pagespeed/all-sites`,
        { headers: getAuthHeaders() }
      );
      setAllSitesData(response.data || []);
    } catch (error) {
      console.error('Failed to fetch all sites PageSpeed:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!currentSite?.id) {
      toast.error('Selectează un site mai întâi');
      return;
    }
    
    setAnalyzing(true);
    setCurrentResult(null); // Clear old result while analyzing
    
    try {
      const response = await axios.get(
        `${API}/pagespeed/analyze/${currentSite.id}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data) {
        setCurrentResult(response.data);
        toast.success('Analiză PageSpeed completă!');
        
        // Refresh history and all sites data
        await Promise.all([
          fetchHistory(),
          fetchAllSitesData()
        ]);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Eroare la analiză';
      toast.error(errorMsg);
      
      // Try to load existing data even if analysis failed
      await fetchLatestResult();
    } finally {
      setAnalyzing(false);
    }
  };

  const handleOptimize = async (fixType) => {
    if (!currentSite?.id) return;
    
    setOptimizing(true);
    setOptimizeResult(null);
    
    try {
      const response = await axios.post(
        `${API}/pagespeed/optimize/${currentSite.id}?fix_type=${fixType}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setOptimizeResult(response.data.results);
      
      const successCount = response.data.results.success?.length || 0;
      const recsCount = response.data.results.recommendations?.length || 0;
      
      if (successCount > 0) {
        toast.success(`${successCount} plugin(uri) instalat(e) cu succes!`);
      }
      if (recsCount > 0) {
        toast.info(`${recsCount} plugin(uri) necesită instalare manuală`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la optimizare');
    } finally {
      setOptimizing(false);
    }
  };

  const handleOptimizeAll = async () => {
    await handleOptimize('all');
  };

  // Format history data for chart - show full date range for selected period
  const chartData = (() => {
    const days = parseInt(historyPeriod);
    const data = [];
    const now = new Date();
    
    // Create array of all dates in the period
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const dateStr = date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' });
      
      // Find analysis for this date (use the latest one if multiple)
      const dayAnalyses = history.filter(h => {
        const hDate = new Date(h.analyzed_at);
        return hDate.toDateString() === date.toDateString();
      });
      
      const latestAnalysis = dayAnalyses.length > 0 
        ? dayAnalyses[dayAnalyses.length - 1] 
        : null;
      
      data.push({
        date: dateStr,
        performance_mobile: latestAnalysis?.mobile?.scores?.performance || null,
        performance_desktop: latestAnalysis?.desktop?.scores?.performance || null,
        seo_mobile: latestAnalysis?.mobile?.scores?.seo || null,
        seo_desktop: latestAnalysis?.desktop?.scores?.seo || null
      });
    }
    
    return data;
  })();

  const currentScores = currentResult?.[selectedStrategy]?.scores || {};
  // Filtrează doar cele mai importante 5 recomandări (sortate după impact)
  const allRecommendations = currentResult?.[selectedStrategy]?.recommendations || [];
  const currentRecommendations = allRecommendations
    .sort((a, b) => {
      // Prioritizează după severitate/impact
      const impactOrder = { 'high': 0, 'medium': 1, 'low': 2 };
      return (impactOrder[a.impact] || 1) - (impactOrder[b.impact] || 1);
    })
    .slice(0, 5); // Doar primele 5

  if (!currentSite) {
    return (
      <div className="space-y-8" data-testid="pagespeed-page">
        <div>
          <h1 className="font-heading text-3xl font-bold">PageSpeed Insights</h1>
          <p className="text-muted-foreground mt-1">
            Analizează performanța site-urilor tale
          </p>
        </div>
        
        {/* All Sites Overview */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Toate Site-urile</CardTitle>
            <CardDescription>Scoruri PageSpeed pentru toate site-urile tale</CardDescription>
          </CardHeader>
          <CardContent>
            {allSitesData.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Niciun site adăugat. Adaugă un site WordPress pentru a începe.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allSitesData.map(site => (
                  <Card key={site.site_id} className={`${site.latest ? getScoreBg(site.latest.mobile?.scores?.performance || 0) : 'bg-secondary'}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium truncate">{site.site_name || site.site_url}</span>
                        {site.latest ? (
                          <Badge className={getScoreBg(site.latest.mobile?.scores?.performance || 0)}>
                            {getScoreLabel(site.latest.mobile?.scores?.performance || 0)}
                          </Badge>
                        ) : (
                          <Badge variant="outline">Neatestat</Badge>
                        )}
                      </div>
                      {site.latest ? (
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.performance || 0)}`}>
                              {site.latest.mobile?.scores?.performance || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">Perf</p>
                          </div>
                          <div>
                            <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.seo || 0)}`}>
                              {site.latest.mobile?.scores?.seo || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">SEO</p>
                          </div>
                          <div>
                            <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.accessibility || 0)}`}>
                              {site.latest.mobile?.scores?.accessibility || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">A11y</p>
                          </div>
                          <div>
                            <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.best_practices || 0)}`}>
                              {site.latest.mobile?.scores?.best_practices || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">BP</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          Selectează site-ul pentru a analiza
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pagespeed-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">PageSpeed Insights</h1>
          <p className="text-muted-foreground mt-1">
            Performanța pentru <span className="text-primary font-medium">{currentSite.site_name || currentSite.site_url}</span>
          </p>
        </div>
        <Button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="analyze-button"
        >
          {analyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Se analizează...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Analizează Acum
            </>
          )}
        </Button>
      </div>

      {/* Strategy Selector */}
      <Tabs value={selectedStrategy} onValueChange={setSelectedStrategy}>
        <TabsList className="bg-secondary">
          <TabsTrigger value="mobile" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Smartphone className="w-4 h-4 mr-2" />
            Mobile
          </TabsTrigger>
          <TabsTrigger value="desktop" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Monitor className="w-4 h-4 mr-2" />
            Desktop
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedStrategy} className="space-y-6 mt-6">
          {!currentResult ? (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Gauge className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nicio analiză</h3>
                <p className="text-muted-foreground text-center mb-6">
                  Apasă "Analizează Acum" pentru a obține scorurile PageSpeed
                </p>
                <Button onClick={handleAnalyze} disabled={analyzing}>
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Analizează
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Scores Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className={`${getScoreBg(currentScores.performance || 0)}`}>
                  <CardContent className="pt-6 flex flex-col items-center">
                    <ScoreGauge score={currentScores.performance || 0} size="large" />
                    <p className="mt-3 font-medium">Performance</p>
                    <p className={`text-sm ${getScoreColor(currentScores.performance || 0)}`}>
                      {getScoreLabel(currentScores.performance || 0)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className={`${getScoreBg(currentScores.seo || 0)}`}>
                  <CardContent className="pt-6 flex flex-col items-center">
                    <ScoreGauge score={currentScores.seo || 0} size="large" />
                    <p className="mt-3 font-medium">SEO</p>
                    <p className={`text-sm ${getScoreColor(currentScores.seo || 0)}`}>
                      {getScoreLabel(currentScores.seo || 0)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className={`${getScoreBg(currentScores.accessibility || 0)}`}>
                  <CardContent className="pt-6 flex flex-col items-center">
                    <ScoreGauge score={currentScores.accessibility || 0} size="large" />
                    <p className="mt-3 font-medium">Accessibility</p>
                    <p className={`text-sm ${getScoreColor(currentScores.accessibility || 0)}`}>
                      {getScoreLabel(currentScores.accessibility || 0)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className={`${getScoreBg(currentScores.best_practices || 0)}`}>
                  <CardContent className="pt-6 flex flex-col items-center">
                    <ScoreGauge score={currentScores.best_practices || 0} size="large" />
                    <p className="mt-3 font-medium">Best Practices</p>
                    <p className={`text-sm ${getScoreColor(currentScores.best_practices || 0)}`}>
                      {getScoreLabel(currentScores.best_practices || 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Recommendations */}
              {currentRecommendations.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-yellow-500" />
                          Top 5 Recomandări Importante
                        </CardTitle>
                        <CardDescription>
                          Cele mai importante îmbunătățiri pentru performanță
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Optimization Results */}
                    {optimizeResult && (
                      <div className="mb-4 p-4 rounded-lg bg-primary/5 border border-primary/20">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          Rezultate Optimizare
                        </h4>
                        {optimizeResult.success?.length > 0 && (
                          <div className="space-y-1 mb-2">
                            {optimizeResult.success.map((item, i) => (
                              <p key={i} className="text-sm text-green-500">
                                ✓ {item.message}
                              </p>
                            ))}
                          </div>
                        )}
                        {optimizeResult.recommendations?.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <p className="text-sm text-muted-foreground">Instalează manual:</p>
                            {optimizeResult.recommendations.map((item, i) => (
                              <a
                                key={i}
                                href={item.manual_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-primary hover:underline"
                              >
                                <ArrowRight className="w-3 h-3" />
                                {item.plugin} - {item.benefit}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      {currentRecommendations.map((rec, index) => (
                        <RecommendationItem 
                          key={rec.id || index} 
                          rec={rec} 
                          onOptimize={handleOptimize}
                          optimizing={optimizing}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Last analyzed */}
              {currentResult.analyzed_at && (
                <p className="text-xs text-muted-foreground text-center">
                  Ultima analiză: {new Date(currentResult.analyzed_at).toLocaleString('ro-RO')}
                </p>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* History Chart - show even with 1 entry, or show message */}
      {(history.length > 0 || currentResult) && (
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <History className="w-5 h-5 text-primary" />
                  Evoluție Scoruri
                </CardTitle>
                <CardDescription>
                  {history.length > 0 
                    ? `${history.length} analize în ultimele ${historyPeriod} zile`
                    : 'Nicio analiză în această perioadă'}
                </CardDescription>
              </div>
              <Select value={historyPeriod} onValueChange={setHistoryPeriod}>
                <SelectTrigger className="w-[140px] bg-secondary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 zile</SelectItem>
                  <SelectItem value="30">30 zile</SelectItem>
                  <SelectItem value="90">90 zile</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                <p>Apasă "Analizează Acum" pentru a genera date pentru grafic</p>
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#A1A1AA" 
                      fontSize={12}
                      interval={Math.floor(chartData.length / 7)}
                    />
                    <YAxis stroke="#A1A1AA" fontSize={12} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0A0A0A', 
                        border: '1px solid #262626', 
                        borderRadius: '8px' 
                      }}
                      formatter={(value) => value !== null ? value : 'N/A'}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="performance_mobile" 
                      name="Performance (Mobile)" 
                      stroke="#22C55E" 
                      strokeWidth={2} 
                      dot={{ r: 4 }}
                      connectNulls={true}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="performance_desktop" 
                      name="Performance (Desktop)" 
                      stroke="#3B82F6" 
                      strokeWidth={2} 
                      dot={{ r: 4 }}
                      connectNulls={true}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="seo_mobile" 
                      name="SEO (Mobile)" 
                      stroke="#F59E0B" 
                      strokeWidth={2} 
                      dot={{ r: 4 }}
                      connectNulls={true}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All Sites Overview */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Toate Site-urile</CardTitle>
          <CardDescription>Comparație scoruri între site-uri</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allSitesData.map(site => (
              <Card 
                key={site.site_id} 
                className={`${site.site_id === currentSite?.id ? 'border-primary' : ''} ${site.latest ? getScoreBg(site.latest.mobile?.scores?.performance || 0) : 'bg-secondary'}`}
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium truncate text-sm">
                      {site.site_name || site.site_url}
                      {site.site_id === currentSite?.id && (
                        <Badge className="ml-2 text-xs">Actual</Badge>
                      )}
                    </span>
                  </div>
                  {site.latest ? (
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.performance || 0)}`}>
                          {site.latest.mobile?.scores?.performance || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">Perf</p>
                      </div>
                      <div>
                        <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.seo || 0)}`}>
                          {site.latest.mobile?.scores?.seo || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">SEO</p>
                      </div>
                      <div>
                        <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.accessibility || 0)}`}>
                          {site.latest.mobile?.scores?.accessibility || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">A11y</p>
                      </div>
                      <div>
                        <p className={`text-lg font-bold ${getScoreColor(site.latest.mobile?.scores?.best_practices || 0)}`}>
                          {site.latest.mobile?.scores?.best_practices || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">BP</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Neanalizat
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
